import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { deriveMapStateFromConfig, TWC } from '@tmapy/beetlejuice-utils';
import type { CallApiResult } from '@tmapy/beetlejuice-utils';
import { useDispatch } from '@tmapy/redux';
import { useMessage, useSelectMessages } from '@tmapy/intl';
import {
  BtnBar,
  CtaBtn,
  CustomSelect,
  DangerAlert,
  Dialog,
  Form,
  FormItem,
  FormRow,
  InfoAlert,
  Input,
  RadioGroup,
  SecondaryBtn,
  SecondaryLink,
  SvgPen,
  Textarea,
  WaitingAlert,
  WarningAlert,
  CloseDialogBtn,
  SuccessAlert,
  MultiSelectProps,
  MultiSelect,
} from '@tmapy/style-guide';

import type { OmittedFormItemProps } from '../../types';
import type {
  NestedGeodesistResponse,
  NestedSubjektResponse,
  PatchedDtmDocUpdateRequest,
  RozsahResponse,
  UserListResponse,
} from '../../openapi/types';
import { DtmDataTypeEnum } from '../../openapi/types/DtmDataTypeEnum';
import { FlowEnum } from '../../openapi/types/FlowEnum';
import {
  DocsRetrieveResponse,
  useDocsRetrieveResource,
} from '../../openapi/operations/docsRetrieve';
import { usersList } from '../../openapi/operations/usersList';
import { docsGetRozsahAktualizacniZmenyExtentPartialUpdate } from '../../openapi/operations/docsGetRozsahAktualizacniZmenyExtentPartialUpdate';
import { geodesistsList } from '../../openapi/operations/geodesistsList';
import { subjektyList } from '../../openapi/operations/subjektyList';
import { dtmDocsSubmitPartialUpdate } from '../../openapi/operations/dtmDocsSubmitPartialUpdate';
import { dtmDocsPartialUpdate } from '../../openapi/operations/dtmDocsPartialUpdate';
import { docsListOperation } from '../../openapi/operations/docsList';
import { dtmDocsCreate } from '../../openapi/operations/dtmDocsCreate';
import { dtmDocsDestroy } from '../../openapi/operations/dtmDocsDestroy';
import { dtmDocsEditPartialUpdate } from '../../openapi/operations/dtmDocsEditPartialUpdate';
import { dtmDocsCancelPartialUpdate } from '../../openapi/operations/dtmDocsCancelPartialUpdate';
import { dtmDocsRejectPartialUpdate } from '../../openapi/operations/dtmDocsRejectPartialUpdate';
import { dtmDocsCloseInterruptPartialUpdate } from '../../openapi/operations/dtmDocsCloseInterruptPartialUpdate';
import { dtmDocsImportInterruptPartialUpdate } from '../../openapi/operations/dtmDocsImportInterruptPartialUpdate';
import { dtmDocsClosePartialUpdate } from '../../openapi/operations/dtmDocsClosePartialUpdate';
import { dtmDocsFinalClosePartialUpdate } from '../../openapi/operations/dtmDocsFinalClosePartialUpdate';
import { dtmDocsImportDocCreate } from '../../openapi/operations/dtmDocsImportDocCreate';
import { dtmDocsEditCheckCreate } from '../../openapi/operations/dtmDocsEditCheckCreate';
import {
  subjektyGetRozsahyEditaceDtiRetrieve,
  SubjektyGetRozsahyEditaceDtiRetrieveResponse,
} from '../../openapi/operations/subjektyGetRozsahyEditaceDtiRetrieve';
import { dtmDocsSendToDmvsPartialUpdate } from '../../openapi/operations/dtmDocsSendToDmvsPartialUpdate';
import { dtmDocsCheckDmvsPendingPozadavkyCreate } from '../../openapi/operations/dtmDocsCheckDmvsPendingPozadavkyCreate';

import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { MapWrapper } from '../../app/components/MapWrapper';
import { MapWithAreas } from '../../areas/components/MapWithAreas';
import { LogList } from '../../logs/components/LogList';
import { HistoryList } from '../../history/components/HistoryList';
import { mapConfig } from '../../manager/mapConfig';

import type { InputProps } from '../../hooks/useInput';
import {
  SSE_TYPE,
  useAppContext,
  useAttachments,
  useAuth,
  useLogDownloadLink,
  useFormItemServerErrors,
  useHistoryAction,
  useInput,
  useMultiSelect,
  useSelect,
  useSelector,
  useSSE,
  useAction,
  toBtnQueryState,
} from '../../hooks';

import { QUERY_STATE } from '../../constants';
import { actionResourceInvalidate } from '../../redux/resource/actions';

import {
  DOC_STATE,
  DOCDETAIL_LOAD_SUCCESS,
  DOCDETAIL_LOAD_WAITING,
  EDITOR_VISIBLE_STATE,
  usableItemsForFormType,
} from '../constants';
import { loadDoc } from '../actions/docDetail';
import { getFormValues } from '../utils/getformvalues';
import { resolveMapUrl } from '../utils/resolveMapUrl';
import { MapReviewButtons } from './MapReviewButtons';

import {
  docFormMessages,
  docTitleMessages,
  rejectionMessages,
  rejectionReasonMessages,
  dtmDocumentationTypesMessages,
  docFormTypSubjektuMessages,
} from './docsMessages';
import { docEditButtonsMsg, docEditMessages, validatorMessages } from './docEditMessages';

import { defaultValues } from './defaultValues';
import { DocAttachments } from './DocAttachments';
import { DocBadge } from './DocBadge';
import { CheckRecordList } from './CheckRecordList';
import { Rejection } from './Rejection';
import { getErrorMessages, ResponseErrorMessage } from './ResponseErrorMessage';
import { DocDownloadFiles } from './DocDownloadFiles';
import { DocInternalInfo } from './DocInternalInfo';

import './Doc.scss';

const renderUserSelectItem = (item: UserListResponse | NestedGeodesistResponse) => (
  <>
    {item.name} {item.surname}
  </>
);

export type DocDtmEditContainerProps = {
  docId?: number;
};

export const DocDtm: React.FC<DocDtmEditContainerProps> = ({ docId }) => {
  useSSE(SSE_TYPE.DOCS, docId);

  const formatMessage = useMessage();
  const dispatch = useDispatch();
  const { appType } = useAppContext();

  const [saveQueryState, setSaveQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [submitQueryState, setSubmitQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [importQueryState, setImportQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [checkQueryState, setCheckQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [closeQueryState, setCloseQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [finalCloseQueryState, seFinalCloseQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [editQueryState, setEditQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [cancelQueryState, setCancelQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [closeInterruptQueryState, setCloseInterruptQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [importInterruptQueryState, setImportInterruptQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [sendToDMVSQueryState, setSendToDMVSQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [checkDmvsPendingPozadavkyQueryState, setCheckDmvsPendingPozadavkyQueryState] =
    useState<QUERY_STATE>(QUERY_STATE.EMPTY);

  const [validate, setValidate] = useState(false);
  const [isRejectionDialogShown, showRejectionDialog] = useState(false);
  const [isLogDialogShown, showLogDialog] = useState(false);
  const [checkLogId, setCheckLogId] = useState<number | undefined>(undefined);
  const [importError, setImportError] = useState<any>(null);
  const [queryMessage, setQueryMessage] = useState<{ code: string; message: string }[] | null>(
    null,
  );

  const config = useSelector((state) => state.app.config);

  const serverErrors = useSelector((state) => state.docedit.errors);
  const removeQueryState = useSelector((state) => state.doc.removeQueryState);
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const auth = useAuth();

  const stateData = useSelector((state) => state.doc.data);
  const attachmentTypes = useAttachments(stateData.attachments);
  const resource = useDocsRetrieveResource(docId ? { id: docId } : null);

  const data = useMemo(() => {
    const cachedData = stateData.id === docId ? stateData : resource.data;
    return docId && cachedData
      ? cachedData
      : { ...defaultValues, state: DOC_STATE.INPROGRESS, flow: FlowEnum.DTM };
  }, [resource.data, stateData, docId]);

  const [mapDataExtent, setMapDataExtent] = useState<number[]>([]);
  const mapState = useMemo(() => deriveMapStateFromConfig({ map: mapConfig() } as TWC), []);
  const mapExtent = useMemo(() => {
    return resource.data?.rozsah_aktualizacni_zmeny_extent.length
      ? resource.data.rozsah_aktualizacni_zmeny_extent
      : mapDataExtent.length
        ? mapDataExtent
        : undefined;
  }, [resource.data?.rozsah_aktualizacni_zmeny_extent, mapDataExtent]);

  const formType = 'podaniDTI';
  const usableItems = usableItemsForFormType[formType];

  const acl = useMemo(
    () => ({
      /**
       * update a partial_update je nutno mit nastaveno stejne (FE,BE), ukladani vzdy jde pres PATCH tedy PartialUpdate
       * partial_update urcuje form disable jednotlivych polozek
       * update urcuje viditelnost ukladaciho tlacitka
       */
      canUpdate: auth.hasPermission('DtmDocs', 'partial_update', data),
      docsUpdate: auth.hasPermission('DtmDocs', 'update', data),
      /**
       * polozka, ktera i pri disable formulare ve stavu DTM_EDITING muze byt zmenena a ulozena
       * ovlivnuje tedy viditelnost tlacitka ulozit
       * !musi byt spravne nastaveno pravidlo partial_update pro BE, aby PTACH probehl
       */
      docsUpdateEditorManager: auth.hasPermission('DtmDocs', 'update.editor_manager', data),

      docsActionShowLog: auth.hasPermission('DtmDocs', 'action.logs', data),
      docsActionChecksVisibility: auth.hasPermission('DtmDocs', 'checks_visibility', data),
      docsActionMapEdit: auth.hasPermission('DtmDocs', 'action.map_edit', data),
      docsActionMapReview: auth.hasPermission('DtmDocs', 'action.map_review', data),

      docsDestroy: auth.hasPermission('DtmDocs', 'destroy', data),
      docsActionSubmit: auth.hasPermission('DtmDocs', 'action.submit', data),
      docsActionImport: auth.hasPermission('DtmDocs', 'action.import_doc', data),
      docsActionImportInterrupt: auth.hasPermission('DtmDocs', 'action.import_interrupt', data),
      docsActionReject: auth.hasPermission('DtmDocs', 'action.reject', data),
      docsActionClose: auth.hasPermission('DtmDocs', 'action.close', data),
      docsActionFinalClose: auth.hasPermission('DtmDocs', 'action.final_close', data),
      docsActionSendToDMVS: auth.hasPermission('DtmDocs', 'action.send_to_dmvs', data),
      docsActionCloseInterrupt: auth.hasPermission('DtmDocs', 'action.close_interrupt', data),
      docsActionCancel: auth.hasPermission('DtmDocs', 'action.cancel', data),
      docsActionEdit: auth.hasPermission('DtmDocs', 'action.edit', data),
      docsActionEditCheck: auth.hasPermission('DtmDocs', 'action.edit_check', data),
      docsAttachmentsISDMVS: auth.hasPermission('DtmDocs', 'attachments_isdmvs', data),

      // download section
      docsActionDownloadCloseLog: auth.hasPermission('DtmDocs', 'download_close_log', data),
      docsActionDownloadImportLog: auth.hasPermission('DtmDocs', 'download_import_log', data),

      // restart dmvs pending
      docsActionCheckDmvsPendingPozadavky: auth.hasPermission(
        'DtmDocs',
        'check_dmvs_pending_pozadavky',
        data,
      ),
    }),
    [auth, data],
  );

  const [showLogDownloadError, setShowLogDownloadError] = useState(false);
  const handleShowDownloadLogError = (visible: boolean) => {
    setShowLogDownloadError(visible);
  };

  const closeLogDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_close_log',
    handleShowDownloadLogError,
  );

  const importLogDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_import_log',
    handleShowDownloadLogError,
  );

  const showLogs = usableItems?.includes('logs') && acl.docsActionShowLog;
  const showChecks = usableItems?.includes('checks') && acl.docsActionChecksVisibility;
  const routeDetail = useHistoryAction('docPodaniDTI', { appType, id: docId! });
  const routeList = useHistoryAction('docList', { appType });

  const canEdit = useMemo(
    () => data.editor_manager?.id === auth.id,
    [data.editor_manager, auth.id],
  );

  useEffect(() => {
    if (!resource.data) {
      dispatch({
        type: DOCDETAIL_LOAD_WAITING,
      });
    } else {
      dispatch({
        type: DOCDETAIL_LOAD_SUCCESS,
        data: resource.data,
      });
      setIsSSEReady(true);
    }
  }, [resource.data, dispatch]);

  const [isSSEReady, setIsSSEReady] = useState(false);

  // after SSE doc state update
  useEffect(() => {
    if (isSSEReady && data.state === 'CLOSING_READY_TO_SEND_TO_DMVS') {
      resource.refetch();
    }
  }, [data.state]);

  // ACTIONS
  const handleRefetch = useCallback(() => {
    resource.refetch();
  }, [resource]);

  const actionQuery = useMemo(() => ({ id: docId }), [docId]);
  const handleActionStart = useCallback(() => setQueryMessage(null), []);
  const handleActionFail = useCallback(
    (response: CallApiResult<any>) => setQueryMessage(getErrorMessages(response.error.data)),
    [],
  );
  const { run: doAction, isActionRunning } = useAction({
    query: actionQuery,
    onRefetch: resource.refetch,
    routeList,
    listPath: docsListOperation().path,
    onStart: handleActionStart,
    onFail: handleActionFail,
  });

  const handleSaveDocWithValidation = async () => {
    setValidate(true);

    const isSomeFormItemInvalid = Object.values(formItems).some((formItem: any) => {
      return formItem.validator?.(formItem.value);
    });

    if (!isSomeFormItemInvalid) {
      return await handleSaveDocWithoutValidation();
    }
    return null;
  };

  const handleSaveDocWithoutValidation = async () => {
    const formValues = getFormValues(formItems);
    const values = {
      ...formValues,
      // select
      geodesist: formValues.geodesist.url || '',
      subjekt: formValues.subjekt.url || '',
      editor_manager: formValues.editor_manager?.url || '',
      // multiselect
      dmvs_rozsahy: formValues.dmvs_rozsahy.map((dmvs_rozsah: any) => dmvs_rozsah.url),
    };

    return await handleSave(values);
  };

  const handleSave = useCallback(
    async (values: PatchedDtmDocUpdateRequest) => {
      setSaveQueryState(QUERY_STATE.WAITING);
      let response;

      try {
        if (docId) {
          response = await dtmDocsPartialUpdate({
            query: { id: docId },
            request: values,
          });

          if (response.ok) {
            setSaveQueryState(QUERY_STATE.SUCCESS);
            handleRefetch();
          } else {
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        } else {
          response = await dtmDocsCreate({
            request: values,
          });
          if (response.ok) {
            setSaveQueryState(QUERY_STATE.SUCCESS);

            routeDetail.replace({ appType, id: response.data.id });
          } else {
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        }
      } finally {
        const docsList = docsListOperation();
        dispatch(actionResourceInvalidate(docsList.path));
      }
      if (response.ok) {
        const attachments = response.data.attachments ?? [];
        return {
          download_attachments_link: '',
          ...response.data,
          attachments,
        };
      }
      return null;
    },
    [docId, routeDetail, appType, dispatch],
  );

  const handleSubmitBtnClick = () =>
    doAction(async (args) => {
      const data = await handleSaveDocWithValidation();
      return data ? dtmDocsSubmitPartialUpdate(args) : null;
    }, setSubmitQueryState);
  const handleDelete = () => {
    doAction(dtmDocsDestroy, setCloseInterruptQueryState, { refetch: false, redirectToList: true });
  };
  const handleRejectBtnClick = () => {
    showRejectionDialog(true);
  };
  const handleRejectCancelBtnClick = () => {
    showRejectionDialog(false);
  };
  const handleCloseInterruptBtnClick = () => {
    doAction(dtmDocsCloseInterruptPartialUpdate, setCloseInterruptQueryState);
  };
  const handleEditCheckBtnClick = () => {
    doAction(dtmDocsEditCheckCreate, setCheckQueryState);
  };
  const handleImportInterruptBtnClick = () => {
    doAction(dtmDocsImportInterruptPartialUpdate, setImportInterruptQueryState);
  };
  const handleCloseBtnClick = () => {
    doAction(dtmDocsClosePartialUpdate, setCloseQueryState);
  };
  const handleFinalCloseBtnClick = () => {
    doAction(dtmDocsFinalClosePartialUpdate, seFinalCloseQueryState);
  };
  const handleEditBtnClick = () => {
    doAction(dtmDocsEditPartialUpdate, setEditQueryState);
  };
  const handleCancelBtnClick = () => {
    doAction(dtmDocsCancelPartialUpdate, setCancelQueryState);
  };
  const handleSendToDMVSBtnClick = () => {
    doAction(dtmDocsSendToDmvsPartialUpdate, setSendToDMVSQueryState);
  };
  const handleCheckDmvsPendingPozadavkyBtnClick = () => {
    doAction(dtmDocsCheckDmvsPendingPozadavkyCreate, setCheckDmvsPendingPozadavkyQueryState);
  };

  const handleCheckClick = useCallback((id: number) => {
    setCheckLogId(id);
    showLogDialog(true);
  }, []);

  const handleImportBtnClick = async () => {
    setImportError(null);
    setImportQueryState(QUERY_STATE.WAITING);
    const importResponse = await dtmDocsImportDocCreate({ query: { id: data.id } });

    if (importResponse.error) {
      setImportQueryState(QUERY_STATE.FAIL);
      setImportError(importResponse.error);

      await Promise.all([handleRefetch(), dispatch(loadDoc(data.id))]);

      setImportQueryState(QUERY_STATE.FAIL);
      setImportError(importResponse.error);
    } else {
      // refresh
      setImportQueryState(QUERY_STATE.SUCCESS);

      await Promise.all([handleRefetch(), dispatch(loadDoc(data.id))]);
    }
  };

  // HOOK FOR INPUTS
  const formItemChangeCallback = useCallback(() => {
    setSaveQueryState(QUERY_STATE.EMPTY);
    setValidate(false);
  }, [setValidate, setSaveQueryState]);

  // NAME
  const name = useInput<InputProps>({
    name: 'name',
    isDisabled: !acl.canUpdate,
    initialValue: data.name,
    label: formatMessage(docFormMessages.name),
    validate: validate,
    validator: (value) =>
      usableItems?.includes('name') && !value
        ? formatMessage(validatorMessages.buildingDescriptionError)
        : null,
    onChange: formItemChangeCallback,
    ...formItemServerErrors.name,
  });

  // DESCRIPTION
  const description = useInput<InputProps>({
    name: 'description',
    isDisabled: !acl.canUpdate,
    initialValue: data.description,
    label: formatMessage(docFormMessages.description),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.description,
  });

  // GEODESIST
  const prepareGeodesistItems = useCallback(async (search: string) => {
    const response = await geodesistsList({ query: { search } });
    if (response.ok) {
      return response.data?.results ?? null;
    }
    return null;
  }, []);
  const geodesist = useSelect<DocsRetrieveResponse['geodesist'], DocsRetrieveResponse['geodesist']>(
    {
      name: 'geodesist',
      isDisabled: !acl.canUpdate,
      label: formatMessage(docFormMessages.geodesist),
      initialValue: data.geodesist,
      onChange: formItemChangeCallback,
      prepareItems: prepareGeodesistItems,
      renderValue: (item) => (item.name || item.surname ? `${item.name} ${item.surname}` : ''),
      renderItem: renderUserSelectItem,
      ...formItemServerErrors.geodesist,
    },
  );

  // DTM DOCUMENTATION TYPE
  const dtmDocumentationTypeItems = useSelectMessages(
    dtmDocumentationTypesMessages,
    DtmDataTypeEnum,
  );

  // filter 'ZPS'
  const dtmDocumentationTypeItemsFiltered = useMemo(() => {
    return dtmDocumentationTypeItems.filter((item) => item.value !== DtmDataTypeEnum.ZPS);
  }, [dtmDocumentationTypeItems]);

  const data_type = useInput({
    name: 'data_type',
    isDisabled: !acl.canUpdate,
    initialValue: data.data_type,
    items: dtmDocumentationTypeItemsFiltered,
    label: formatMessage(docFormMessages.data_type),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.data_type,
    validate: validate,
    validator: (value) =>
      usableItems?.includes('data_type') && !value
        ? formatMessage(validatorMessages.dataTypeError)
        : null,
  });

  // SUBJEKT
  const renderSubjektSelectItem = (item: NestedSubjektResponse) => (
    <>{`${item.jmeno} (${item.id_subjektu})`}</>
  );
  const prepareSubjektyItems = useCallback(async (search: string) => {
    const response = await subjektyList({ query: { search } });
    if (response.ok) {
      const data = response.data?.results;
      if (data) {
        dmvs_rozsahy.refresh();

        return response.data?.results;
      }
      return null;
    }
    return null;
  }, []);
  const renderSubjektValue = useCallback((item: { jmeno?: string; id_subjektu?: string }) => {
    const jmeno = item.jmeno || '';
    const idSubjektu = item.id_subjektu ? ` (${item.id_subjektu})` : '';
    return `${jmeno}${idSubjektu}`;
  }, []);
  const subjektChangeCallback = useCallback(() => {
    setSaveQueryState(QUERY_STATE.EMPTY);
    setValidate(false);

    dmvs_rozsahy.clear();
    dmvs_rozsahy.refresh();
  }, [setValidate, setSaveQueryState]);

  const subjekt = useSelect<DocsRetrieveResponse['subjekt'], DocsRetrieveResponse['subjekt']>({
    name: 'subjekt',
    isDisabled: !acl.canUpdate,
    label: formatMessage(docFormMessages.subjekt_id),
    initialValue: data.subjekt,
    validate: validate,
    validator: (value) => (!value?.id ? formatMessage(validatorMessages.subjektError) : null),
    onChange: subjektChangeCallback,
    prepareItems: prepareSubjektyItems,
    renderValue: renderSubjektValue,
    renderItem: renderSubjektSelectItem,
    ...formItemServerErrors.subjekt,
  });

  // EDITOR MANAGER
  const prepareEditorManagerItems = useCallback(async (search: string) => {
    const response = await usersList({
      query: { search, roles: ['ADMIN'], is_editor: true, is_active: true },
    });
    if (response.ok) {
      return response.data?.results;
    }
    return null;
  }, []);
  const editor_manager = useSelect<DocsRetrieveResponse['editor_manager'], UserListResponse>({
    name: 'editor_manager',
    label: formatMessage(docFormMessages.editor_manager),
    initialValue: data.editor_manager,
    onChange: formItemChangeCallback,
    prepareItems: prepareEditorManagerItems,
    renderValue: (item) => (item?.name || item?.surname ? [item.name, item.surname].join(' ') : ''),
    renderItem: renderUserSelectItem,
    ...formItemServerErrors.editor_manager,
  });

  // ROZSAH EDITACE
  const renderRozsahEditaceItem = (item: SubjektyGetRozsahyEditaceDtiRetrieveResponse) => {
    const typySubjektu = item.subjekty.map((subjekt) => {
      const typySubjektuMessages =
        subjekt.typy_subjektu?.map((item) =>
          formatMessage.lookup(docFormTypSubjektuMessages, item),
        ) ?? [];
      return `${subjekt.nazev} (${typySubjektuMessages.join(',')})`;
    });

    return (
      <div>
        <div>{`${item.nazev} (${item.id_rozsahu})`}</div>
        <div className='sg-u-type-xsm sg-u-c-body-darker'>{`${typySubjektu.join(', ')}`}</div>
      </div>
    );
  };
  const subjektId = subjekt?.value?.id || data.subjekt?.id;
  const prepareRozsahEditaceItems = useCallback(
    async (search: string) => {
      if (!subjektId) return null;

      const response = await subjektyGetRozsahyEditaceDtiRetrieve({
        query: { id: subjektId, search },
      });
      if (response.ok) {
        // TODO https://tmapy.visualstudio.com/tmapy/_workitems/edit/49774
        return ((response.data as any).results as RozsahResponse[]) ?? null;
      }
      return null;
    },
    [subjektId],
  );

  const renderRozsahEditaceValue = useCallback((item: RozsahResponse) => {
    return `${item.nazev} (${item.id_rozsahu})`;
  }, []);
  const dmvs_rozsahy = useMultiSelect<
    MultiSelectProps<SubjektyGetRozsahyEditaceDtiRetrieveResponse> | OmittedFormItemProps,
    SubjektyGetRozsahyEditaceDtiRetrieveResponse
  >({
    name: 'dmvs_rozsahy',
    isDisabled: !acl.canUpdate,
    label: formatMessage(docFormMessages.dmvs_rozsahy),
    initialValue: resource.data?.dmvs_rozsahy as any,
    onChange: formItemChangeCallback,
    prepareItems: prepareRozsahEditaceItems,
    renderValue: renderRozsahEditaceValue,
    renderItem: renderRozsahEditaceItem,
    validate,
    ...formItemServerErrors.tags,
  });

  const state = { value: data.state };
  const prejimka_type = { value: data.prejimka_type };
  const formItems = {
    state,
    prejimka_type,
    name,
    data_type,
    description,
    geodesist,
    subjekt,
    editor_manager,
    dmvs_rozsahy,
  } as const;

  // ATTACHMENTS
  const uploadHelpText = formatMessage.fallback(['document.podaniDTI.uploadHelpText']);

  // MAP EDITOR
  useEffect(() => {
    (async () => {
      if (data.rozsah_aktualizacni_zmeny_extent.length) {
        setMapDataExtent(data.rozsah_aktualizacni_zmeny_extent);
        return;
      }

      if (docId && (acl.docsActionMapEdit || acl.docsActionMapReview)) {
        const result = await docsGetRozsahAktualizacniZmenyExtentPartialUpdate({
          query: { id: docId },
          request: {},
        });
        if (result.ok) {
          setMapDataExtent(result.data.rozsah_aktualizacni_zmeny_extent);
        }
      }
    })();
  }, [
    docId,
    data.rozsah_aktualizacni_zmeny_extent,
    acl.docsActionMapEdit,
    acl.docsActionMapReview,
  ]);

  const hashParams = useMemo(
    () => (mapDataExtent.length ? `#bbox=${mapDataExtent}` : ''),
    [mapDataExtent],
  );

  // MAP EDITOR
  const editorConfigUrl =
    data_type.value === DtmDataTypeEnum.TI
      ? config.editorTI?.url
      : data_type.value === DtmDataTypeEnum.DI
        ? config.editorDI?.url
        : undefined;
  const editorUrl = resolveMapUrl(editorConfigUrl, `/${docId}/${hashParams}`);

  // MAP REVIEWER
  const canMapReview = !!docId && acl.docsActionMapReview;
  const reviewUrl = useMemo(
    () => resolveMapUrl(config.reviewer.url, `-tidi/${docId}/${hashParams}`),
    [config.reviewer.url, docId, hashParams],
  );
  const review3dUrl = useMemo(
    () => resolveMapUrl(config.reviewer.url3d, `-tidi/?doc_id=${docId}`),
    [config.reviewer.url3d, docId],
  );

  const handleCancelLogDialogBtnClick = useCallback(() => {
    setCheckLogId(undefined);
    showLogDialog(false);
  }, []);

  return (
    <MainContent width='full' key={`${resource.data?.id}`}>
      {(resource.queryState === QUERY_STATE.SUCCESS || !docId) && (
        <article>
          <div className='dmx-doc--head sg-u-vs-2'>
            <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
              <header className='sg-a-d-f sg-a-ai-c sg-a-g-1 sg-a-fw-w'>
                <PageTitle>
                  {formatMessage.lookup(docTitleMessages, formType, {
                    id: data.id || 'new',
                  })}
                </PageTitle>
                {data.state && (
                  <DocBadge
                    docState={data.state}
                    flow={data.flow}
                    rejectedBySubjekty={data.rejected_by_subjekty}
                  />
                )}
              </header>
              <BtnBar>
                {(acl.docsUpdate || acl.docsUpdateEditorManager) && (
                  <CtaBtn
                    queryState={toBtnQueryState(saveQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSaveDocWithValidation}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnSave} />
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionSubmit && (
                  <CtaBtn
                    queryState={toBtnQueryState(submitQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSubmitBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnSubmit} />
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionImport && (
                  <CtaBtn
                    queryState={toBtnQueryState(importQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleImportBtnClick}
                  >
                    {data.state === DOC_STATE.IMPORT_INTERRUPTED ||
                    data.state === DOC_STATE.IMPORT_FAILED ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnImport} />
                    )}
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionEdit && (
                  <CtaBtn
                    queryState={toBtnQueryState(editQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleEditBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnEdit} />
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionEditCheck && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(checkQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleEditCheckBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnEditCheck} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionCloseInterrupt && (
                  <SecondaryBtn
                    isDanger
                    queryState={toBtnQueryState(closeInterruptQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCloseInterruptBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnInterrupt} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionImportInterrupt && (
                  <SecondaryBtn
                    isDanger
                    queryState={toBtnQueryState(importInterruptQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleImportInterruptBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnInterrupt} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionClose && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(closeQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCloseBtnClick}
                  >
                    {data.state === DOC_STATE.CLOSE_INTERRUPTED ||
                    data.state === DOC_STATE.CLOSE_FAILED ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnClose} />
                    )}
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionFinalClose && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(finalCloseQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleFinalCloseBtnClick}
                  >
                    {data.state === DOC_STATE.CLOSE_FINAL_FAILED ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnFinalClose} />
                    )}
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionSendToDMVS && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(sendToDMVSQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSendToDMVSBtnClick}
                  >
                    {data.state === DOC_STATE.CLOSING_READY_TO_SEND_TO_DMVS ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnSendToDMVS} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnNotifyClose} />
                    )}
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionReject && (
                  <SecondaryBtn
                    isDanger
                    isDisabled={isActionRunning}
                    onClick={handleRejectBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnReject} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionCancel && (
                  <SecondaryBtn
                    isDanger
                    queryState={toBtnQueryState(cancelQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCancelBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnCancel} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsDestroy && (
                  <SecondaryBtn
                    isDisabled={isActionRunning || removeQueryState === QUERY_STATE.WAITING}
                    isDanger
                    onClick={handleDelete}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnRemove} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionCheckDmvsPendingPozadavky && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(checkDmvsPendingPozadavkyQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCheckDmvsPendingPozadavkyBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnCheckDmvsPendingPozadavky} />
                  </SecondaryBtn>
                )}
              </BtnBar>
            </div>
            {showLogDownloadError && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.fileDownloadError} />
              </DangerAlert>
            )}
            {(resource.queryState === QUERY_STATE.WAITING ||
              saveQueryState === QUERY_STATE.WAITING ||
              submitQueryState === QUERY_STATE.WAITING ||
              closeQueryState === QUERY_STATE.WAITING ||
              finalCloseQueryState === QUERY_STATE.WAITING ||
              closeInterruptQueryState === QUERY_STATE.WAITING ||
              importInterruptQueryState === QUERY_STATE.WAITING ||
              cancelQueryState === QUERY_STATE.WAITING ||
              checkQueryState === QUERY_STATE.WAITING ||
              sendToDMVSQueryState === QUERY_STATE.WAITING ||
              checkDmvsPendingPozadavkyQueryState === QUERY_STATE.WAITING ||
              editQueryState === QUERY_STATE.WAITING) && (
              <WaitingAlert>
                <FormattedMessage {...docEditMessages.queryStateWaiting} />
              </WaitingAlert>
            )}
            {(cancelQueryState === QUERY_STATE.FAIL ||
              submitQueryState === QUERY_STATE.FAIL ||
              checkDmvsPendingPozadavkyQueryState === QUERY_STATE.FAIL) && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.queryStateFail} />
              </DangerAlert>
            )}

            {saveQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.submitQueryStateFail} />
              </DangerAlert>
            )}

            {saveQueryState === QUERY_STATE.SUCCESS && (
              <SuccessAlert>
                <FormattedMessage {...docEditMessages.submitQueryStateSuccess} />
              </SuccessAlert>
            )}
            {formItemServerErrors.non_field_errors && (
              <DangerAlert>{formItemServerErrors.non_field_errors.errorMessage!}</DangerAlert>
            )}
            {resource.queryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.loadQueryStateFail} />
              </DangerAlert>
            )}

            {data.state === DOC_STATE.REJECTED &&
              resource.queryState === QUERY_STATE.SUCCESS &&
              data.rejection_reason && (
                <WarningAlert>
                  <b className='sg-u-type-bold'>
                    <FormattedMessage {...rejectionMessages.reasonTitle} />
                    {' - '}
                    <FormattedMessage
                      {...rejectionReasonMessages[data.rejection_reason ?? 'null']}
                    />
                  </b>
                  {data.rejection_note && (
                    <p className='dmx-doc--warningAlertText sg-a-mt-1/2'>{data.rejection_note}</p>
                  )}
                </WarningAlert>
              )}

            {closeQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.closeQueryStateFail} />
              </DangerAlert>
            )}
            {finalCloseQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.finalCloseQueryStateFail} />
              </DangerAlert>
            )}
            {sendToDMVSQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.sendToDMVSDtmQueryStateFail} />
                {queryMessage?.map((msg, index) => (
                  <React.Fragment key={index}>
                    <br />
                    <ResponseErrorMessage error={msg} />
                  </React.Fragment>
                ))}
              </DangerAlert>
            )}
            {checkQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.checkQueryStateFail} />
              </DangerAlert>
            )}
            {editQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.editQueryStateFail} />
              </DangerAlert>
            )}
            {(closeInterruptQueryState === QUERY_STATE.FAIL ||
              importInterruptQueryState === QUERY_STATE.FAIL) && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.interruptQueryStateFail} />
              </DangerAlert>
            )}

            {!!docId && acl.docsActionMapEdit && editorUrl && !canEdit && (
              <WarningAlert>
                <FormattedMessage {...docEditMessages.canNotEdit} />
              </WarningAlert>
            )}
          </div>
          <div className='dmx-doc--cols'>
            <div className='sg-u-vs-4'>
              <Form onSubmit={handleSaveDocWithValidation}>
                <div className='sg-u-vs-4'>
                  <section>
                    <h3 className='dmx-doc--attributesTitle sg-u-type-h2 sg-a-ml-2'>&nbsp;</h3>
                    <div className='sg-u-box sg-a-p-2 sg-u-vs-2 sg-a-mt-1'>
                      {usableItems?.includes('name') && (
                        <FormRow>
                          <FormItem {...name} isRequired>
                            <Input {...name} />
                          </FormItem>
                        </FormRow>
                      )}
                      {usableItems?.includes('description') && (
                        <FormRow>
                          <FormItem {...description}>
                            <Textarea {...description} />
                          </FormItem>
                        </FormRow>
                      )}
                      {usableItems?.includes('geodesist') && (
                        <FormRow>
                          <FormItem {...geodesist}>
                            <CustomSelect {...geodesist} />
                          </FormItem>
                        </FormRow>
                      )}
                      {usableItems?.includes('data_type') && (
                        <FormRow>
                          <FormItem {...data_type} isRequired>
                            <RadioGroup {...data_type} direction='row' />
                          </FormItem>
                        </FormRow>
                      )}
                      {usableItems?.includes('editor_manager') &&
                        (acl.docsUpdateEditorManager ||
                          EDITOR_VISIBLE_STATE.includes(data.state)) && (
                          <FormRow>
                            <FormItem {...editor_manager}>
                              <CustomSelect {...editor_manager} />
                            </FormItem>
                          </FormRow>
                        )}
                      {usableItems?.includes('subjekt') && (
                        <FormRow>
                          <FormItem {...subjekt} isRequired>
                            <CustomSelect {...subjekt} />
                          </FormItem>
                        </FormRow>
                      )}

                      {usableItems?.includes('dmvs_rozsahy') && (
                        <FormRow>
                          <FormItem {...dmvs_rozsahy}>
                            <MultiSelect {...dmvs_rozsahy} />
                          </FormItem>
                        </FormRow>
                      )}
                    </div>
                  </section>
                </div>
              </Form>

              {resource.data && <DocInternalInfo doc={resource.data} />}

              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...docFormMessages.attachments} />
                </h3>
                <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-u-vs-3'>
                  {usableItems?.includes('attachments') && (
                    <section>
                      <h3 className='sg-u-type-h2 sg-a-ml-2'>
                        <FormattedMessage {...docFormMessages.attachments_docs} />
                      </h3>
                      <div className='sg-u-box sg-a-d-ib sg-a-mw-100% sg-a-p-2 sg-a-mt-1 sg-u-vs-2'>
                        {uploadHelpText && (
                          <div className='sg-a-mw-fc'>
                            <InfoAlert>{uploadHelpText}</InfoAlert>
                          </div>
                        )}
                        <DocAttachments
                          doc={data}
                          prejimkaType={data.prejimka_type}
                          createDoc={handleSaveDocWithoutValidation}
                          attachments={attachmentTypes.regular}
                          importError={importError}
                          importQueryState={importQueryState}
                        />
                      </div>
                    </section>
                  )}
                  {usableItems?.includes('attachments_isdmvs') && acl.docsAttachmentsISDMVS && (
                    <section className='sg-a-mt-2'>
                      <h3 className='sg-u-type-h2 sg-a-ml-2'>
                        <FormattedMessage {...docFormMessages.attachments_isdmvs} />
                      </h3>
                      <div className='sg-u-box sg-a-d-ib sg-a-mw-100% sg-a-p-2 sg-a-mt-1 sg-u-vs-2'>
                        <DocAttachments
                          doc={data}
                          prejimkaType={data.prejimka_type}
                          attachments={attachmentTypes.isdmvs}
                        />
                      </div>
                    </section>
                  )}
                  <section className='sg-a-mt-2'>
                    <DocDownloadFiles
                      title={<FormattedMessage {...docFormMessages.attachments_system} />}
                      docId={docId}
                      items={[
                        {
                          permission: acl.docsActionDownloadCloseLog,
                          link: closeLogDownloadLink,
                          label: <FormattedMessage {...docEditButtonsMsg.docBtnDownloadLog} />,
                          ext: 'zip',
                        },
                        {
                          permission: acl.docsActionDownloadImportLog,
                          link: importLogDownloadLink,
                          label: <FormattedMessage {...docEditButtonsMsg.docBtnDownloadLog} />,
                          ext: 'zip',
                        },
                      ]}
                    />
                  </section>
                </div>
              </section>
            </div>

            <div className='dmx-doc--rightCol sg-u-vs-4'>
              {(acl.docsActionMapReview || acl.docsActionMapEdit) && mapState && (
                <section className='sg-a-f-a sg-a-d-f sg-a-fd-c sg-a-rg-1'>
                  <div className='sg-a-d-f sg-a-jc-sb sg-a-ai-c sg-a-fw-w sg-a-cg-2'>
                    <h3 className='sg-u-type-h2 sg-a-ml-2'>
                      <FormattedMessage {...docFormMessages.docAreaTitle} />
                    </h3>
                    <BtnBar>
                      {!!docId && acl.docsActionMapEdit && editorUrl && canEdit && (
                        <SecondaryLink
                          href={editorUrl}
                          target='_blank'
                          icon={{ element: <SvgPen /> }}
                        >
                          <FormattedMessage {...docEditButtonsMsg.docBtnMapEditShort} />
                        </SecondaryLink>
                      )}
                      {canMapReview && (
                        <MapReviewButtons reviewUrl={reviewUrl} review3dUrl={review3dUrl} />
                      )}
                    </BtnBar>
                  </div>
                  <div className='sg-u-box sg-a-p-2 sg-a-f-a'>
                    <MapWrapper>
                      <MapWithAreas state={mapState} extent={mapExtent} />
                    </MapWrapper>
                  </div>
                </section>
              )}

              {resource?.data?.checks && resource?.data?.checks.length > 0 && showChecks && (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2'>
                    <FormattedMessage {...docFormMessages.checks} />
                  </h3>
                  <div className='sg-u-box sg-a-mt-1 dmx-a-mw-fc'>
                    <CheckRecordList
                      docId={docId}
                      items={resource?.data?.checks}
                      onClick={handleCheckClick}
                      onRefetch={handleRefetch}
                    />
                  </div>
                </section>
              )}

              {showLogs && (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                    <FormattedMessage {...docFormMessages.logs} />
                  </h3>
                  <LogList objectId={docId!} resourceType={'doc'} />
                </section>
              )}

              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                  <FormattedMessage {...docFormMessages.history} />
                </h3>
                <HistoryList history={data.history} />
              </section>
            </div>
          </div>

          {isLogDialogShown && (
            <Dialog
              onEscKeyDown={handleCancelLogDialogBtnClick}
              onOverlayClick={handleCancelLogDialogBtnClick}
            >
              <CloseDialogBtn onClick={handleCancelLogDialogBtnClick} />
              <div className='sg-a-p-4 sg-u-vs-2'>
                <PageTitle>
                  <FormattedMessage {...docFormMessages.logs} />
                </PageTitle>
                <LogList objectId={checkLogId!} resourceType={'dtm'} />
              </div>
            </Dialog>
          )}

          {isRejectionDialogShown && !!docId && (
            <Dialog width='500px'>
              <Rejection
                docId={docId}
                prejimkaType={data.prejimka_type}
                onCancelBtnClick={handleRejectCancelBtnClick}
                resource={resource}
                partialUpdateCallback={dtmDocsRejectPartialUpdate}
              />
            </Dialog>
          )}
        </article>
      )}
    </MainContent>
  );
};
